/**
 * KanbanView — 5-column board with @dnd-kit drag & drop.
 *
 * Columns map to dossier statuses:
 *   PROCESSING / INCOMPLET / A_VERIFIER / COMPLET / TRAITE
 * (ERREUR is folded into A_VERIFIER for visual simplicity)
 */
import React, { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Archive as ArchiveIcon,
  Bell,
  PartyPopper,
  Database,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { TagList } from "./DossierTags";

const COLUMNS = [
  { id: "PROCESSING", label: "En cours", icon: Loader2, color: "border-norea-border bg-norea-bg" },
  { id: "INCOMPLET", label: "En attente de traitement", icon: Bell, color: "border-status-incompletBorder bg-status-incompletBg" },
  { id: "A_VERIFIER", label: "À vérifier", icon: AlertTriangle, color: "border-status-verifierBorder bg-status-verifierBg" },
  { id: "COMPLET", label: "En attente de CQ", icon: CheckCircle2, color: "border-status-completBorder bg-status-completBg" },
  { id: "IMPORTE", label: "Importés Chelly", icon: Database, color: "border-teal-200 bg-teal-50" },
  { id: "TRAITE", label: "Archivés", icon: ArchiveIcon, color: "border-norea-borderStrong bg-norea-surfaceHover" },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}j`;
}

function DossierCard({ d, dragging }) {
  const navigate = useNavigate();
  const ex = d.extraction || {};
  const validated = d.validated_count || 0;
  const total = (d.attachments || []).length;
  const isStale = d.status === "INCOMPLET" && d.created_at && (Date.now() - new Date(d.created_at).getTime()) > 7 * 86400 * 1000;
  const lastRelance = d.last_relance_at;

  return (
    <div
      className={`bg-white border rounded-md p-3 mb-2 cursor-grab active:cursor-grabbing transition-shadow ${
        dragging ? "shadow-2xl rotate-1 ring-2 ring-norea-primary" : "shadow-sm hover:shadow-md"
      } ${isStale ? "border-status-erreurBorder border-l-4" : "border-norea-border"}`}
      onClick={(e) => {
        // Only navigate on plain click, not on drag end
        if (e.defaultPrevented) return;
        navigate(`/dossiers/${d.id}`);
      }}
      data-testid={`kanban-card-${d.id}`}
    >
      <div className="font-medium text-sm text-norea-text truncate">
        {ex.nom || "INCONNU"} {ex.prenom || ""}
      </div>
      <TagList ids={d.tags} />
      <div className="text-[11px] text-norea-textMuted flex items-center gap-1.5 mt-0.5 flex-wrap">
        <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-norea-surfaceHover text-norea-text border border-norea-border rounded">
          {d.produit || "?"}
        </span>
        <span>{ex.organisme_financier || "—"}</span>
        <span>·</span>
        <span>{d.partenaire || "—"}</span>
      </div>
      {d.chelly?.status && <div className="text-[10px] text-teal-700 mt-0.5">Chelly : {d.chelly.status}</div>}
      <div className="text-[11px] text-norea-textMuted mt-1 flex items-center gap-1.5">
        <Clock className="w-3 h-3" />
        Reçu il y a {timeAgo(d.created_at)}
        {isStale && <span className="text-status-erreurText font-medium">· urgent (>7j)</span>}
      </div>
      {total > 0 && (
        <div className="mt-1.5 flex items-center gap-2">
          <div className="flex-1 h-1 bg-norea-bg rounded-full overflow-hidden">
            <div
              className="h-full bg-norea-primary transition-all"
              style={{ width: `${total ? (validated / total) * 100 : 0}%` }}
            />
          </div>
          <span className="text-[10px] tabular-nums text-norea-textMuted">{validated}/{total}</span>
          {validated === total && total > 0 && (
            <PartyPopper className="w-3 h-3 text-emerald-600" />
          )}
        </div>
      )}
      {lastRelance && (
        <div className="text-[10px] text-norea-textMuted mt-1.5 flex items-center gap-1">
          <Bell className="w-3 h-3" />
          {d.relance_count || 1} relance(s) · dernière il y a {timeAgo(lastRelance)}
        </div>
      )}
    </div>
  );
}

function DraggableCard({ d }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: d.id });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={{ opacity: isDragging ? 0.3 : 1 }}>
      <DossierCard d={d} dragging={false} />
    </div>
  );
}

function Column({ col, dossiers, total }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  const Icon = col.icon;
  return (
    <div className="flex-1 min-w-[260px] flex flex-col" data-testid={`kanban-col-${col.id}`}>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-t-md border-x border-t ${col.color}`}>
        <Icon className={`w-3.5 h-3.5 ${col.id === "PROCESSING" ? "animate-spin" : ""}`} />
        <span className="font-heading text-sm font-medium text-norea-text">{col.label}</span>
        <span className="ml-auto text-xs tabular-nums text-norea-textMuted bg-white border border-norea-border rounded-full px-2 py-0.5" title={total > dossiers.length ? `${dossiers.length} affichés sur ${total}` : undefined} data-testid={`kanban-count-${col.id}`}>
          {total > dossiers.length ? `${dossiers.length} / ${total}` : dossiers.length}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={`flex-1 min-h-[200px] p-2 border-x border-b rounded-b-md ${col.color} ${isOver ? "ring-2 ring-norea-primary ring-inset" : ""}`}
      >
        {dossiers.map((d) => (
          <DraggableCard key={d.id} d={d} />
        ))}
        {dossiers.length === 0 && (
          <div className="text-center text-xs text-norea-textMuted py-8 italic">Vide</div>
        )}
      </div>
    </div>
  );
}

export default function KanbanView({ items, onChange, statusCounts }) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
    items.forEach((d) => {
      let col = d.status;
      if (col === "ERREUR") col = "A_VERIFIER"; // fold errors into "À vérifier"
      if (g[col]) g[col].push(d);
      else g["A_VERIFIER"]?.push(d);
    });
    return g;
  }, [items]);

  const activeItem = activeId ? items.find((d) => d.id === activeId) : null;

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !active) return;
    const targetStatus = over.id;
    const draggedId = active.id;
    const item = items.find((d) => d.id === draggedId);
    if (!item || item.status === targetStatus) return;
    if (!COLUMNS.find((c) => c.id === targetStatus)) return;
    // Optimistic update via parent
    onChange?.({ id: draggedId, status: targetStatus });
    try {
      await api.post(`/dossiers/${draggedId}/status`, { status: targetStatus });
      toast.success(`Déplacé vers "${COLUMNS.find((c) => c.id === targetStatus).label}"`, {
        duration: 1500,
      });
    } catch (e) {
      toast.error("Impossible de changer le statut");
      onChange?.({ id: draggedId, status: item.status }); // revert
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-view">
        {COLUMNS.map((col) => (
          <Column key={col.id} col={col} dossiers={grouped[col.id]} total={(statusCounts?.[col.id] || 0) + (col.id === "A_VERIFIER" ? statusCounts?.ERREUR || 0 : 0)} />
        ))}
      </div>
      <DragOverlay>
        {activeItem ? <DossierCard d={activeItem} dragging /> : null}
      </DragOverlay>
    </DndContext>
  );
}
